import { useState, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout';
import { Button } from '@/components/ui';
import { PhotoUploadModal } from '@/features/photos';
import { useEffectiveBusinessId } from '@/hooks';
import { useProject } from '../hooks/useProject';
import { useUpdateProject } from '../hooks/useUpdateProject';
import { ProjectStatusBadge } from '../components/ProjectStatusBadge';
import { ProjectEditForm } from '../components/ProjectEditForm';
import { ProjectPhotoGallery } from '../components/ProjectPhotoGallery';
import { ProjectCustomersSection } from '../components/ProjectCustomersSection';
import type { ProjectPhotoGalleryRef } from '../components/ProjectPhotoGallery';
import type { ProjectStatus } from '../types';
import { ProjectTripsSection } from '@/features/trips';
import type { ProjectTripsSectionRef } from '@/features/trips';
import {
  ClientCommunicationsSection,
  CommunicationUploadModal,
} from '@/features/client-communications';
import type { ClientCommunicationsSectionRef } from '@/features/client-communications';
import {
  CustomerInvoiceAttachmentsSection,
  CustomerInvoiceAttachmentUploadModal,
} from '@/features/customer-invoice-attachments';
import type { CustomerInvoiceAttachmentsSectionRef } from '@/features/customer-invoice-attachments';
import {
  SupplierCostDocumentsSection,
  SupplierCostDocumentUploadModal,
} from '@/features/supplier-cost-documents';
import type { SupplierCostDocumentsSectionRef } from '@/features/supplier-cost-documents';

export function ProjectDetailPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [isEditingProject, setIsEditingProject] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const photoGalleryRef = useRef<ProjectPhotoGalleryRef>(null);
  const tripsRef = useRef<ProjectTripsSectionRef>(null);
const [isCommunicationUploadModalOpen, setIsCommunicationUploadModalOpen] = useState(false);
const communicationsRef = useRef<ClientCommunicationsSectionRef>(null);
const [isCustomerDocumentUploadModalOpen, setIsCustomerDocumentUploadModalOpen] = useState(false);
const [isSupplierDocumentUploadModalOpen, setIsSupplierDocumentUploadModalOpen] = useState(false);
const customerDocumentsRef = useRef<CustomerInvoiceAttachmentsSectionRef>(null);
const supplierDocumentsRef = useRef<SupplierCostDocumentsSectionRef>(null);

  const { project, isLoading, error, notFound, refetch } = useProject({
    projectId: projectId || null,
  });

  const {
    isUpdating: isUpdatingProject,
    error: updateProjectError,
    updateProjectData,
    clearError: clearProjectError,
  } = useUpdateProject();

  const effectiveBusinessId = useEffectiveBusinessId();

  // Format date for display (handles timezone correctly)
const formatDate = (dateString: string | null): string => {
  if (!dateString) return '—';
  
  // Parse as local date to avoid timezone shift
  // dateString format: "2025-12-16"
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day); // month is 0-indexed
  
  return date.toLocaleDateString('en-CA', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

  // Project edit handlers
  const handleStartEditProject = () => {
    clearProjectError();
    setIsEditingProject(true);
  };

  const handleCancelEditProject = () => {
    clearProjectError();
    setIsEditingProject(false);
  };

  const handleSaveProject = async (data: {
    project_name: string;
    status: ProjectStatus;
    start_date: string | null;
    end_date: string | null;
  }) => {
    if (!projectId) return;

    const result = await updateProjectData(projectId, data);

    if (result) {
      setIsEditingProject(false);
      await refetch();
    }
  };

  // Photo upload handlers
  const handleOpenUploadModal = () => {
    setIsUploadModalOpen(true);
  };

  const handleCloseUploadModal = () => {
    setIsUploadModalOpen(false);
  };

  const handleUploadComplete = () => {
    // Refresh the photo gallery
    photoGalleryRef.current?.refetch();
  };

// Communication upload handlers
const handleOpenCommunicationUploadModal = () => {
  setIsCommunicationUploadModalOpen(true);
};

const handleCloseCommunicationUploadModal = () => {
  setIsCommunicationUploadModalOpen(false);
};

const handleCommunicationUploadComplete = () => {
  // Refresh the communications section
  communicationsRef.current?.refetch();
};

// Customer document upload handlers
const handleOpenCustomerDocumentUploadModal = () => {
  setIsCustomerDocumentUploadModalOpen(true);
};

const handleCloseCustomerDocumentUploadModal = () => {
  setIsCustomerDocumentUploadModalOpen(false);
};

const handleCustomerDocumentUploadComplete = () => {
  // Refresh the customer documents section
  customerDocumentsRef.current?.refetch();
};

// Supplier document upload handlers
const handleOpenSupplierDocumentUploadModal = () => {
  setIsSupplierDocumentUploadModalOpen(true);
};

const handleCloseSupplierDocumentUploadModal = () => {
  setIsSupplierDocumentUploadModalOpen(false);
};

const handleSupplierDocumentUploadComplete = () => {
  // Refresh the supplier documents section
  supplierDocumentsRef.current?.refetch();
};

  // Loading state
  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-secondary-200 rounded w-1/3" />
            <div className="bg-white rounded-xl border border-secondary-200 p-6 space-y-4">
              <div className="h-6 bg-secondary-200 rounded w-1/2" />
              <div className="h-4 bg-secondary-200 rounded w-3/4" />
              <div className="h-4 bg-secondary-200 rounded w-1/4" />
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Not found state
  if (notFound) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto text-center py-12">
          <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-secondary-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-secondary-900">
            Project Not Found
          </h1>
          <p className="text-secondary-600 mt-2 mb-6">
            The project you're looking for doesn't exist or you don't have
            access to it.
          </p>
          <Button variant="primary" onClick={() => navigate('/projects')}>
            Back to Projects
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  // Error state
  if (error) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto">
          <div className="p-6 bg-red-50 border border-red-200 rounded-lg text-center">
            <h2 className="text-lg font-semibold text-red-800">
              Error Loading Project
            </h2>
            <p className="text-red-700 mt-1">{error}</p>
            <Button
              variant="outline"
              onClick={() => navigate('/projects')}
              className="mt-4"
            >
              Back to Projects
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!project) return null;

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        {/* Page Header */}
        <div className="mb-6">
          <Link
            to="/projects"
            className="inline-flex items-center gap-1 text-sm text-secondary-500 
                       hover:text-secondary-700 transition-colors mb-2"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Projects
          </Link>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-secondary-900">
                {project.customer_name}
              </h1>
              <p className="text-secondary-600 mt-1">{project.project_name}</p>
              {/* Site Location (address and city only) */}
              {(project.site_street_address || project.customer_billing_street_address) && (
                <p className="text-sm text-secondary-500 mt-1 flex items-center gap-1">
                  <svg className="w-4 h-4 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  {project.site_street_address || project.customer_billing_street_address}, {project.site_city || project.customer_billing_city}
                </p>
              )}
            </div>
            {!isEditingProject && <ProjectStatusBadge status={project.status} />}
          </div>
        </div>

        {/* Quick Upload Actions */}
        <div className="mb-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            
            {/* Project Photos Upload Button */}
            <button
              onClick={handleOpenUploadModal}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-white border border-secondary-200
                         rounded-lg text-sm font-medium text-secondary-700 hover:bg-secondary-50
                         hover:border-secondary-300 transition-colors"
            >
              <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="hidden sm:inline">Project Photos</span>
              <span className="sm:hidden">Photos</span>
            </button>

            {/* Customer Comms Button */}
            <button
              onClick={handleOpenCommunicationUploadModal}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-white border border-secondary-200
                         rounded-lg text-sm font-medium text-secondary-700 hover:bg-secondary-50
                         hover:border-secondary-300 transition-colors"
            >
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <span className="hidden sm:inline">Communication</span>
              <span className="sm:hidden">Comms</span>
            </button>

            {/* Supplier Document Button */}
            <button
              onClick={handleOpenSupplierDocumentUploadModal}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-white border border-secondary-200
                         rounded-lg text-sm font-medium text-secondary-700 hover:bg-secondary-50
                         hover:border-secondary-300 transition-colors"
            >
              <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2zM10 8.5a.5.5 0 11-1 0 .5.5 0 011 0zm5 5a.5.5 0 11-1 0 .5.5 0 011 0z" />
              </svg>
              <span className="hidden sm:inline">Supplier Doc</span>
              <span className="sm:hidden">Supplier</span>
            </button>

            {/* Customer Document Upload Button */}
            <button
              onClick={handleOpenCustomerDocumentUploadModal}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-white border border-secondary-200
                         rounded-lg text-sm font-medium text-secondary-700 hover:bg-secondary-50
                         hover:border-secondary-300 transition-colors"
            >
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="hidden sm:inline">Customer Doc</span>
              <span className="sm:hidden">Customer</span>
            </button>
          </div>
        </div>

        {/* Project Details Card */}
        <div className="bg-white rounded-xl border border-secondary-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-secondary-900">
              Project Details
            </h2>
            {!isEditingProject && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleStartEditProject}
              >
                <svg
                  className="w-4 h-4 mr-1.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
                Edit
              </Button>
            )}
          </div>

          {isEditingProject ? (
            <ProjectEditForm
              project={project}
              onSave={handleSaveProject}
              onCancel={handleCancelEditProject}
              isSubmitting={isUpdatingProject}
              error={updateProjectError}
            />
          ) : (
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
              <div>
                <dt className="text-sm font-medium text-secondary-500">
                  Status
                </dt>
                <dd className="mt-1">
                  <ProjectStatusBadge status={project.status} size="sm" />
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-secondary-500">
                  Business
                </dt>
                <dd className="mt-1 text-secondary-900">
                  {project.business_name || '—'}
                </dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-sm font-medium text-secondary-500">
                  Site Location
                </dt>
                <dd className="mt-1 text-secondary-900">
                  {(project.site_street_address || project.customer_billing_street_address) ? (
                    <>
                      {/* Street address with unit */}
                      {project.site_street_address || project.customer_billing_street_address}
                      {(project.site_unit || project.customer_billing_unit) && `, ${project.site_unit || project.customer_billing_unit}`}
                      <br />
                      {/* City, Province  Postal Code */}
                      {project.site_city || project.customer_billing_city}, {project.site_province || project.customer_billing_province}
                      {(project.site_postal_code || project.customer_billing_postal_code) && `  ${project.site_postal_code || project.customer_billing_postal_code}`}
                    </>
                  ) : (
                    '—'
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-secondary-500">
                  Start Date
                </dt>
                <dd className="mt-1 text-secondary-900">
                  {formatDate(project.start_date)}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-secondary-500">
                  End Date
                </dt>
                <dd className="mt-1 text-secondary-900">
                  {formatDate(project.end_date)}
                </dd>
              </div>
            </dl>
          )}
        </div>

        {/* Photo Gallery Card */}
        <div className="bg-white rounded-xl border border-secondary-200 p-6 mt-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-secondary-900">
              Project Photos
            </h2>
            <Button
              variant="outline"
              size="sm"
              onClick={handleOpenUploadModal}
              disabled={isEditingProject}
            >
              <svg
                className="w-4 h-4 mr-1.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                />
              </svg>
              Upload Photos
            </Button>
          </div>
          <ProjectPhotoGallery ref={photoGalleryRef} projectId={project.id} />
        </div>

        {/* Customer Invoice Documents Card */}
        <div className="bg-white rounded-xl border border-secondary-200 p-6 mt-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-secondary-900">
              Customer Invoice Documents
            </h2>
            <Button
              variant="outline"
              size="sm"
              onClick={handleOpenCustomerDocumentUploadModal}
              disabled={isEditingProject}
            >
              <svg
                className="w-4 h-4 mr-1.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Add
            </Button>
          </div>
          <CustomerInvoiceAttachmentsSection
            ref={customerDocumentsRef}
            projectId={project.id}
          />
        </div>

        {/* Supplier Cost Documents Card */}
        <div className="bg-white rounded-xl border border-secondary-200 p-6 mt-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-secondary-900">
              Supplier Cost Documents
            </h2>
            <Button
              variant="outline"
              size="sm"
              onClick={handleOpenSupplierDocumentUploadModal}
              disabled={isEditingProject}
            >
              <svg
                className="w-4 h-4 mr-1.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Add
            </Button>
          </div>
          <SupplierCostDocumentsSection
            ref={supplierDocumentsRef}
            projectId={project.id}
          />
        </div>

        {/* Client Communications Card */}
        <div className="bg-white rounded-xl border border-secondary-200 p-6 mt-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-secondary-900">
              Client Communications
            </h2>
            <Button
              variant="outline"
              size="sm"
              onClick={handleOpenCommunicationUploadModal}
              disabled={isEditingProject}
            >
              <svg
                className="w-4 h-4 mr-1.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Add
            </Button>
          </div>
          <ClientCommunicationsSection
            ref={communicationsRef}
            projectId={project.id}
          />
        </div>

        {/* Project Customers Card */}
        <div className="bg-white rounded-xl border border-secondary-200 p-6 mt-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-secondary-900">
              Project Customers
            </h2>
            <Link
              to="/customers"
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              Manage Customers →
            </Link>
          </div>
          <ProjectCustomersSection
            projectId={project.id}
            businessId={effectiveBusinessId || ''}
            disabled={isEditingProject}
          />
        </div>

        

        {/* Trips Card */}
        <div className="bg-white rounded-xl border border-secondary-200 p-6 mt-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-secondary-900">
              Assigned Trips
            </h2>
            <Link
              to="/trips"
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              Manage Trips →
            </Link>
          </div>
          <ProjectTripsSection ref={tripsRef} projectId={project.id} />
        </div>

        {/* Actions (only show when not editing) */}
        {!isEditingProject && (
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Button variant="outline" onClick={() => navigate('/projects')}>
              Back to Projects
            </Button>
          </div>
        )}

        {/* Photo Upload Modal */}
        {project && (
          <PhotoUploadModal
            isOpen={isUploadModalOpen}
            onClose={handleCloseUploadModal}
            projectId={project.id}
            projectName={project.project_name}
            onUploadComplete={handleUploadComplete}
          />
        )}

        {/* Communication Upload Modal */}
        {project && (
          <CommunicationUploadModal
            isOpen={isCommunicationUploadModalOpen}
            onClose={handleCloseCommunicationUploadModal}
            projectId={project.id}
            projectName={project.project_name}
            onUploadComplete={handleCommunicationUploadComplete}
          />
        )}

        {/* Customer Invoice Document Upload Modal */}
        {project && (
          <CustomerInvoiceAttachmentUploadModal
            isOpen={isCustomerDocumentUploadModalOpen}
            onClose={handleCloseCustomerDocumentUploadModal}
            projectId={project.id}
            projectName={project.project_name}
            onUploadComplete={handleCustomerDocumentUploadComplete}
          />
        )}

        {/* Supplier Cost Document Upload Modal */}
        {project && (
          <SupplierCostDocumentUploadModal
            isOpen={isSupplierDocumentUploadModalOpen}
            onClose={handleCloseSupplierDocumentUploadModal}
            projectId={project.id}
            projectName={project.project_name}
            onUploadComplete={handleSupplierDocumentUploadComplete}
          />
        )}

      </div>
    </DashboardLayout>
  );
}